import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { mapDtoToSpreadsheet, mapDtoToWorksheet, SpreadsheetApiResponse, WorksheetDto } from './excel-data-mappers';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ConnectionInfo, SimpleReport, SpreadsheetPage } from '@mode/dataset/excel/contract';

@Injectable({
  providedIn: 'root',
})
export class ExcelClientService {
  constructor(private http: HttpClient) {}

  getExcelReport(username: string): Observable<SimpleReport> {
    return this.http.get<SimpleReport>(`/api/${username}/reports/new?only=token,layout`);
  }

  getExcelConnection(username: string, token: string): Observable<ConnectionInfo> {
    return this.http.get<{ email_address: string }>(`/api/${username}/users/${token}/excel_connection`).pipe(
      map(({ email_address }) => ({
        userConnected: true,
        email: email_address,
      })),
      catchError((err) => {
        return of({
          userConnected: err.status !== 403,
          email: '',
        });
      })
    );
  }

  getExcelSpreadsheets(username: string, token: string, searchText?: string): Observable<SpreadsheetPage> {
    const searchParam = searchText ? `?search_text=${searchText}` : '';
    return this.http.get<SpreadsheetApiResponse>(`/api/${username}/users/${token}/excel_sheets${searchParam}`).pipe(
      catchError(() => of<SpreadsheetApiResponse>({ _embedded: { spreadsheets: [] } })),
      map((response) => ({
        spreadsheets: response._embedded.spreadsheets.map(mapDtoToSpreadsheet),
        nextPage: response._links?.next_page?.href,
      }))
    );
  }

  getExcelWorksheets(username: string, token: string, spreadsheetId: string): Observable<WorksheetDto[]> {
    return this.http
      .get<{
        _embedded: { worksheets: WorksheetDto[] };
      }>(`/api/${username}/users/${token}/excel_sheets/${spreadsheetId}/excel_worksheets`)
      .pipe(
        map(({ _embedded: { worksheets } }) => worksheets.map(mapDtoToWorksheet)),
        catchError(() => of<WorksheetDto[]>([]))
      );
  }

  loadMoreSpreadsheets(nextPageUrl: string, currentSpreadsheets: readonly any[]): Observable<SpreadsheetPage> {
    return this.http.get<SpreadsheetApiResponse>(nextPageUrl).pipe(
      catchError(() => of<SpreadsheetApiResponse>({ _embedded: { spreadsheets: [] } })),
      map((response) => ({
        spreadsheets: currentSpreadsheets.concat(response._embedded.spreadsheets.map(mapDtoToSpreadsheet)),
        nextPage: response._links?.next_page?.href,
      }))
    );
  }
}
